import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Check, ChevronsUpDown, ListFilter } from 'lucide-react';
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@roger/ui-components';

import {
  TASK_TYPE_CREATABLE_FILTER_TABS,
  filterCreatableTaskTypeOptions,
  hasActiveTaskTypeFilter,
  type CreatableTaskTypeOption,
  type TaskTypeFilterTab,
  type TaskTypeFilterValue,
} from './taskTypeFilters';
import { TaskTypeFilter } from './TaskTypeFilter';

interface TaskTypeComboboxProps {
  id?: string;
  options: CreatableTaskTypeOption[];
  value: string;
  onValueChange: (value: CreatableTaskTypeOption) => void;
  filter: TaskTypeFilterValue;
  onFilterChange: (filter: TaskTypeFilterValue) => void;
  tabs?: ReadonlyArray<TaskTypeFilterTab>;
  disabled?: boolean;
  modal?: boolean;
  placeholder?: string;
  /** Controlled open state. Omit for the default uncontrolled behaviour. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const TaskTypeCombobox = ({
  id,
  options,
  value,
  onValueChange,
  filter,
  onFilterChange,
  tabs = TASK_TYPE_CREATABLE_FILTER_TABS,
  disabled = false,
  modal = false,
  placeholder = 'Select task type',
  open: controlledOpen,
  onOpenChange,
}: TaskTypeComboboxProps) => {
  const popupId = useId();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  // Support both controlled (parent passes `open`) and uncontrolled usage.
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = useCallback(
    (nextOpen: boolean) => {
      setUncontrolledOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [onOpenChange]
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [focusedOptionId, setFocusedOptionId] = useState<string | null>(null);
  const [isKeyboardNavActive, setIsKeyboardNavActive] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const focusedOptionIdRef = useRef<string | null>(null);

  const selectedOption = options.find(option => option.id === value);
  const selectedLabel = selectedOption?.display_name ?? (value || placeholder);
  const filteredOptions = useMemo(
    () => filterCreatableTaskTypeOptions(options, filter, searchQuery),
    [filter, options, searchQuery]
  );
  const focusedIndex = useMemo(
    () => filteredOptions.findIndex(option => option.id === focusedOptionId),
    [filteredOptions, focusedOptionId]
  );
  const setFocusedOption = useCallback((optionId: string | null) => {
    focusedOptionIdRef.current = optionId;
    setFocusedOptionId(optionId);
  }, []);

  const getDefaultFocusedOptionId = useCallback(
    (candidateOptions: CreatableTaskTypeOption[]): string | null => {
      if (!candidateOptions.length) {
        return null;
      }

      const selectedIndex = candidateOptions.findIndex(
        option => option.id === value
      );

      return candidateOptions[selectedIndex >= 0 ? selectedIndex : 0].id;
    },
    [value]
  );
  const getFocusedIndex = useCallback(
    () =>
      filteredOptions.findIndex(
        option => option.id === focusedOptionIdRef.current
      ),
    [filteredOptions]
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!filteredOptions.length) {
      setFocusedOption(null);
      return;
    }

    if (
      focusedOptionIdRef.current &&
      filteredOptions.some(option => option.id === focusedOptionIdRef.current)
    ) {
      return;
    }

    setFocusedOption(getDefaultFocusedOptionId(filteredOptions));
  }, [filteredOptions, getDefaultFocusedOptionId, open, setFocusedOption]);

  useEffect(() => {
    if (!open || !isKeyboardNavActive || focusedIndex < 0) {
      return;
    }

    const focusedItem = listRef.current?.querySelector<HTMLElement>(
      `[data-task-option-index="${focusedIndex}"]`
    );

    focusedItem?.scrollIntoView({ block: 'nearest' });
  }, [focusedIndex, isKeyboardNavActive, open]);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);

    if (nextOpen) {
      setIsKeyboardNavActive(false);
      setFocusedOption(getDefaultFocusedOptionId(filteredOptions));
      return;
    }

    setSearchQuery('');
    setFocusedOption(null);
    setIsKeyboardNavActive(false);
  };

  const handleOptionSelect = (optionId: string) => {
    const selected = options.find(option => option.id === optionId);
    if (!selected) {
      return;
    }

    onValueChange(selected);
    setSearchQuery('');
    setFocusedOption(null);
    setIsKeyboardNavActive(false);
    setOpen(false);
  };

  const handleFilterChange = (nextFilter: TaskTypeFilterValue) => {
    onFilterChange(nextFilter);
    setIsKeyboardNavActive(false);
    setFocusedOption(
      getDefaultFocusedOptionId(
        filterCreatableTaskTypeOptions(options, nextFilter, searchQuery)
      )
    );
    window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
  };
  const handleCommandKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!filteredOptions.length) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        setIsKeyboardNavActive(true);
        const currentFocusedIndex = getFocusedIndex();
        const nextIndex =
          currentFocusedIndex < 0
            ? 0
            : Math.min(currentFocusedIndex + 1, filteredOptions.length - 1);
        setFocusedOption(filteredOptions[nextIndex].id);
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        setIsKeyboardNavActive(true);
        const currentFocusedIndex = getFocusedIndex();
        const nextIndex =
          currentFocusedIndex < 0
            ? filteredOptions.length - 1
            : Math.max(currentFocusedIndex - 1, 0);
        setFocusedOption(filteredOptions[nextIndex].id);
        break;
      }
      case 'Enter': {
        const currentFocusedIndex = getFocusedIndex();
        if (
          currentFocusedIndex < 0 ||
          currentFocusedIndex >= filteredOptions.length
        ) {
          return;
        }

        event.preventDefault();
        handleOptionSelect(filteredOptions[currentFocusedIndex].id);
        break;
      }
      case ' ':
      case 'Spacebar': {
        if (!isKeyboardNavActive) {
          return;
        }

        const currentFocusedIndex = getFocusedIndex();
        if (
          currentFocusedIndex < 0 ||
          currentFocusedIndex >= filteredOptions.length
        ) {
          event.preventDefault();
          return;
        }

        event.preventDefault();
        handleOptionSelect(filteredOptions[currentFocusedIndex].id);
        break;
      }
      default:
        break;
    }
  };

  const handleListWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    const listElement = listRef.current;
    if (!listElement || listElement.scrollHeight <= listElement.clientHeight) {
      return;
    }

    listElement.scrollTop += event.deltaY;
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <Popover modal={modal} open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type='button'
          variant='outline'
          size='default'
          role='combobox'
          aria-label={selectedLabel}
          aria-expanded={open}
          aria-haspopup='dialog'
          aria-controls={open ? popupId : undefined}
          disabled={disabled}
          className='w-full justify-between'
        >
          <span className='truncate text-sm'>{selectedLabel}</span>
          <span className='ml-2 flex shrink-0 items-center gap-1'>
            <ListFilter
              className={`h-4 w-4 ${
                hasActiveTaskTypeFilter(filter)
                  ? 'text-foreground'
                  : 'text-muted-foreground'
              }`}
              aria-hidden='true'
            />
            <ChevronsUpDown
              className='h-4 w-4 text-muted-foreground'
              aria-hidden='true'
            />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        id={popupId}
        className='w-[var(--radix-popover-trigger-width)] p-0'
        align='start'
      >
        <Command shouldFilter={false} onKeyDown={handleCommandKeyDown}>
          <CommandInput
            ref={searchInputRef}
            placeholder='Search task types...'
            className='border-b'
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <TaskTypeFilter
            variant='combobox'
            value={filter}
            onChange={handleFilterChange}
            tabs={tabs}
          />
          <CommandList
            ref={listRef}
            className='max-h-60 overscroll-contain'
            onMouseDown={() => setIsKeyboardNavActive(false)}
            onMouseMove={() => setIsKeyboardNavActive(false)}
            onWheelCapture={handleListWheel}
          >
            <CommandEmpty className='px-3 py-2 text-center text-sm text-muted-foreground'>
              No task types match these filters.
            </CommandEmpty>
            <CommandGroup>
              {filteredOptions.map((option, index) => (
                <CommandItem
                  key={option.id}
                  data-task-option-index={index}
                  value={option.id}
                  className={
                    focusedIndex === index
                      ? 'bg-accent text-accent-foreground'
                      : isKeyboardNavActive
                        ? 'aria-selected:bg-transparent aria-selected:text-foreground'
                        : undefined
                  }
                  onMouseEnter={() => {
                    setIsKeyboardNavActive(false);
                    setFocusedOption(option.id);
                  }}
                  onSelect={(selectedId: string) =>
                    handleOptionSelect(selectedId)
                  }
                >
                  <Check
                    className={`mr-2 h-4 w-4 ${
                      value === option.id ? 'opacity-100' : 'opacity-0'
                    }`}
                    aria-hidden='true'
                  />
                  <span className='truncate text-sm'>
                    {option.display_name}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
